import { Lock, Warehouse } from 'lucide-react';
import { usePrivateWarehouseStock } from '@/hooks/use-products';
import { formatCurrency, formatNumber } from '@/lib/utils';

/**
 * Same card shape and left-rail treatment as the location-scoped cards
 * (scopeCardCls), but in sky blue with a light tint so the private warehouse
 * stands apart from the store / public warehouse inventory cards above it —
 * this stock belongs to another company and is read-only here. Still
 * location-scoped (data-scope) for spotlight mode.
 */
const PRIVATE_CARD_CLS =
  'relative rounded-lg border border-l-[3px] border-sky-200 border-l-sky-500 bg-sky-50/60 p-6 pl-[calc(1.5rem+1px)]';

/**
 * Read-only stock + selling price at other companies' private warehouses that
 * super admin has mapped to the given store locations. Kept separate from the
 * (public) Warehouse Inventory section on purpose — the two are never merged.
 * Renders nothing when no private warehouse is mapped.
 */
export function PrivateWarehouseSection({
  productId,
  locationIds,
}: {
  productId: string | undefined;
  locationIds: string[];
}) {
  const { data, isLoading } = usePrivateWarehouseStock(productId, locationIds);
  const rows = data?.data ?? [];
  if (!productId || locationIds.length === 0 || isLoading || rows.length === 0) return null;

  return (
    <div className={PRIVATE_CARD_CLS} data-scope="location" data-testid="private-warehouse-section">
      <div className="mb-1 flex flex-wrap items-center gap-2">
        <Warehouse className="h-4 w-4 text-sky-600" />
        <h3 className="text-base font-semibold">Warehouse Inventory - Private</h3>
        <span className="inline-flex items-center gap-1 rounded border border-sky-200 bg-sky-100 px-1.5 py-0.5 text-[10px] font-medium text-sky-800">
          <Lock className="h-3 w-3" /> read-only
        </span>
      </div>
      <p className="mb-4 text-xs text-muted-foreground">
        Stock available to order from private warehouses linked to this location.
      </p>
      <div className="overflow-x-auto rounded-lg border border-sky-200 bg-white">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-sky-200 bg-sky-50 text-xs text-muted-foreground">
              <th className="px-3 py-2 text-left font-medium">Warehouse</th>
              <th className="px-3 py-2 text-right font-medium">In Stock</th>
              <th className="px-3 py-2 text-right font-medium">Selling / Club Price</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.warehouseLocationId} className="border-b border-border last:border-0">
                <td className="px-3 py-2 font-medium">{r.warehouseName}</td>
                <td className="px-3 py-2 text-right tabular-nums">
                  {r.inStockQty === null ? (
                    <span className="text-xs text-muted-foreground">{r.mapped ? 'Not stocked' : 'Not carried'}</span>
                  ) : (
                    <>
                      {formatNumber(r.inStockQty)}
                      {r.unit ? <span className="ml-1 text-xs text-muted-foreground">{r.unit}</span> : null}
                    </>
                  )}
                </td>
                <td className="px-3 py-2 text-right tabular-nums">
                  {r.priceHidden ? (
                    <span className="text-xs text-muted-foreground">Hidden</span>
                  ) : r.sellingPrice === null ? (
                    '—'
                  ) : (
                    <>
                      {formatCurrency(r.sellingPrice)}
                      {r.unit ? <span className="ml-1 text-xs text-muted-foreground">/ {r.unit}</span> : null}
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
